import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import LogoImage from "@/components/LogoImage";
import { api } from "@/lib/api";
import { hasMarketingConsent, onConsentChange } from "@/components/CookieBanner";

export function useAds() {
  return useQuery({
    queryKey: ["ads"],
    queryFn: async () => (await api.get("/ads")).data,
    staleTime: 30000,
    refetchInterval: 60000,
  });
}

const SLOT_STYLES = {
  header: "h-[50px] md:h-[90px] max-w-[728px] w-full mx-auto",
  sidebar: "h-[250px] w-full max-w-[300px] mx-auto",
  infeed: "h-[250px] w-full max-w-[300px] mx-auto",
  footer: "h-[60px] md:h-[90px] max-w-[728px] w-full mx-auto",
};

const SLOT_LABELS = {
  header: "LEADERBOARD 728x90",
  sidebar: "RECTANGLE 300x250",
  infeed: "IN-FEED 300x250",
  footer: "LEADERBOARD 728x90",
};

export default function AdBanner({ slot, className = "" }) {
  const { data } = useAds();
  const conf = data?.[slot];
  const [marketingOk, setMarketingOk] = useState(hasMarketingConsent());

  useEffect(() => onConsentChange(() => setMarketingOk(hasMarketingConsent())), []);

  if (conf && conf.enabled === false) return null;

  // Script pubblicitari di terze parti (es. AdSense) solo dopo consenso marketing (GDPR)
  if (conf?.html && marketingOk) {
    return (
      <div
        data-testid={`ad-${slot}`}
        className={`${SLOT_STYLES[slot]} overflow-hidden ${className}`}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(conf.html, {
            ADD_TAGS: ["ins"],
            ADD_ATTR: ["data-ad-client", "data-ad-slot", "data-ad-format", "data-full-width-responsive"],
            ALLOWED_URI_REGEXP: /^https?:\/\//i,
          }),
        }}
      />
    );
  }

  if (conf?.image_url) {
    return (
      <a
        data-testid={`ad-${slot}`}
        href={conf.link || "#"}
        target="_blank"
        rel="noopener noreferrer sponsored"
        className={`block ${SLOT_STYLES[slot]} overflow-hidden rounded-lg border border-[#3F3F4C] ${className}`}
      >
        <LogoImage src={conf.image_url} alt="Sponsor" hideOnError className="w-full h-full object-cover" />
      </a>
    );
  }

  return (
    <div
      data-testid={`ad-${slot}`}
      className={`${SLOT_STYLES[slot]} flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-[#3F3F4C] bg-[#1A1A1E]/80 checkered-fade ${className}`}
    >
      <span className="font-body-sport text-[10px] font-bold tracking-[0.3em] text-gray-500 uppercase">
        Spazio Sponsor
      </span>
      <span className="font-time text-[9px] text-gray-600">{SLOT_LABELS[slot]}</span>
    </div>
  );
}
